import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getReactionDisplay } from '../../utils/reactionConfig';
import SpacePicker from '../tasks/SpacePicker';
import { VOICE_TASK_EVENT, matchChannelBySpokenName } from '../../utils/voiceTaskCommands';
import { sendMessage, addReaction } from '../../store/slices/chatSlice';
import { fetchTasks } from '../../store/slices/taskSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { fetchChannels } from '../../store/slices/channelSlice';
import { isWorkloopDm } from '../../utils/directMessageUtils';
import { isAttendanceChannelType, isGroupWorkChannel, isNotificationsChannel } from '../../utils/channelTypeUtils';

const escapeHtml = (text) =>
  String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const CreateWorkflowDialog = ({
  isOpen,
  typeId,
  initialDescription = '',
  saving = false,
  defaultSpaceId = '',
  defaultSpaceName = '',
  fromVoice = false,
  onClose,
  onCreate,
}) => {
  const dispatch = useDispatch();
  const groupChannels = useSelector((state) => state.channels.channels || []);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [spaceId, setSpaceId] = useState('');
  const [spaceName, setSpaceName] = useState('');
  const [channelId, setChannelId] = useState('');
  const titleRef = useRef(null);
  const formInitedRef = useRef(false);
  const handleCreateRef = useRef(null);
  const typeDisplay = typeId ? getReactionDisplay(typeId) : null;
  const publishChannels = (Array.isArray(groupChannels) ? groupChannels : []).filter((channel) => (
    isGroupWorkChannel(channel)
    && !isWorkloopDm(channel)
    && !isAttendanceChannelType(channel)
    && !isNotificationsChannel(channel)
    && channel?.readOnly !== true
    && !String(channel?.systemChannelKey || '').trim()
  ));
  const selectedChannel = publishChannels.find((channel) => String(channel.id) === String(channelId));
  const canCreate = Boolean(title.trim()) && !saving && (!fromVoice || Boolean(channelId));
  const titlePlaceholder = fromVoice
    ? 'Speak: add title …  (or keep title as …)'
    : 'Give this workflow a title';
  const descriptionPlaceholder = fromVoice
    ? 'Speak: add description …  (or keep description as …)'
    : 'Workflow description';

  useEffect(() => {
    formInitedRef.current = false;
  }, [typeId]);

  useEffect(() => {
    if (!isOpen) {
      formInitedRef.current = false;
      setTitle('');
      setDescription('');
      setChannelId('');
      return undefined;
    }
    if (formInitedRef.current) return undefined;
    formInitedRef.current = true;
    setTitle('');
    setDescription(initialDescription || '');
    setSpaceId(defaultSpaceId || '');
    setSpaceName(defaultSpaceName || '');
    setChannelId('');
    if (fromVoice) dispatch(fetchChannels());
    const timer = window.setTimeout(() => titleRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [dispatch, fromVoice, isOpen, initialDescription, typeId, defaultSpaceId, defaultSpaceName]);

  const handleClose = () => {
    if (saving) return;
    setTitle('');
    setDescription('');
    setSpaceId('');
    setSpaceName('');
    setChannelId('');
    onClose?.();
  };

  const handleCreate = (overrideChannelId) => {
    const nextTitle = title.trim();
    const targetChannelId = String(overrideChannelId || channelId || '').trim();
    if (!nextTitle || saving) return;
    if (fromVoice && !targetChannelId) {
      dispatch(addNotification({
        type: 'info',
        message: 'Say which chat group, like “put in engineering”',
      }));
      return;
    }
    onCreate?.({
      title: nextTitle.slice(0, 200),
      description: description.trim(),
      spaceId,
      spaceName,
      channelId: targetChannelId,
    });
  };

  handleCreateRef.current = handleCreate;

  useEffect(() => {
    const onVoice = (event) => {
      const action = event.detail?.action;
      const value = String(event.detail?.value || '').trim();
      const append = Boolean(event.detail?.append);
      if (!action || action === 'open' || action === 'openWorkflow') return;
      if (!isOpen) return;
      if (action === 'title' && value) setTitle(value.slice(0, 200));
      if (action === 'description' && value) {
        setDescription((prev) => (append && prev ? `${prev} ${value}`.trim() : value));
      }
      if (action === 'channel' && value) {
        const matched = matchChannelBySpokenName(value, publishChannels);
        if (matched?.id) {
          setChannelId(matched.id);
          dispatch(addNotification({
            type: 'success',
            message: `Publishing in #${matched.name}`,
          }));
          handleCreateRef.current?.(matched.id);
        } else {
          dispatch(addNotification({
            type: 'info',
            message: `Could not match chat group “${value}”`,
          }));
        }
      }
      if (action === 'publish') {
        handleCreateRef.current?.();
      }
    };
    window.addEventListener(VOICE_TASK_EVENT, onVoice);
    return () => window.removeEventListener(VOICE_TASK_EVENT, onVoice);
  }, [dispatch, isOpen, publishChannels]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1100] overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full transform transition-all"
          role="dialog"
          aria-labelledby="create-workflow-title"
        >
          <div className="flex items-center justify-between gap-3 p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 id="create-workflow-title" className="text-lg font-semibold text-gray-900 dark:text-white min-w-0 truncate">
              Create {typeDisplay?.label || 'workflow'}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              <SpacePicker
                variant="combo"
                valueId={spaceId}
                valueName={spaceName}
                disabled={saving}
                onChange={({ spaceId: nextId, spaceName: nextName }) => {
                  setSpaceId(nextId || '');
                  setSpaceName(nextName || '');
                }}
              />
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Workflow type
              </label>
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/60 text-sm font-medium text-gray-900 dark:text-white">
                {typeDisplay?.emoji ? <span aria-hidden="true">{typeDisplay.emoji}</span> : null}
                <span>{typeDisplay?.label || 'Workflow'}</span>
              </div>
            </div>

            <div>
              <label htmlFor="workflow-create-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title <span className="text-rose-500">*</span>
              </label>
              <input
                ref={titleRef}
                id="workflow-create-title"
                type="text"
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
                placeholder={titlePlaceholder}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>

            <div>
              <label htmlFor="workflow-create-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                id="workflow-create-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={descriptionPlaceholder}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-y"
              />
            </div>

            {fromVoice ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Chat group <span className="text-rose-500">*</span>
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {selectedChannel
                    ? `Selected: #${selectedChannel.name}`
                    : 'Speak: put in [group]'}
                </p>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {publishChannels.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-gray-500">No chat groups found</div>
                  ) : (
                    publishChannels.map((channel) => {
                      const selected = String(channel.id) === String(channelId);
                      return (
                        <button
                          key={channel.id}
                          type="button"
                          onClick={() => setChannelId(channel.id)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                            selected
                              ? 'bg-primary-50/80 dark:bg-primary-900/20 text-primary-800 dark:text-primary-100'
                              : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/60'
                          }`}
                        >
                          <span className="text-gray-400">#</span>
                          <span className="truncate">{channel.name}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={!canCreate}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Creating…' : 'Create workflow'}
              </button>
            </div>
            {fromVoice ? (
              <p className="mt-2 text-right text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                Speak: put in [group]
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const VoiceWorkflowDialogHost = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onVoice = (event) => {
      if (event.detail?.action !== 'openWorkflow') return;
      const nextType = String(event.detail?.value || '').trim();
      if (!nextType) return;
      window.__workloopVoiceWorkflowOpen = true;
      setTypeId(nextType);
      setOpen(true);
    };
    window.addEventListener(VOICE_TASK_EVENT, onVoice);
    return () => window.removeEventListener(VOICE_TASK_EVENT, onVoice);
  }, []);

  useEffect(() => {
    window.__workloopVoiceWorkflowOpen = open;
    return () => {
      if (!open) window.__workloopVoiceWorkflowOpen = false;
    };
  }, [open]);

  const handleCreate = useCallback(async ({ title, description, spaceId, spaceName, channelId }) => {
    const nextTitle = String(title || '').trim();
    const targetChannelId = String(channelId || '').trim();
    if (!nextTitle || !typeId || saving) return;
    if (!targetChannelId) {
      dispatch(addNotification({
        type: 'info',
        message: 'Say which chat group, like “put in engineering”',
      }));
      return;
    }
    const desc = String(description || '').trim();
    const content = desc
      ? `<p>${escapeHtml(nextTitle)}</p><p>${escapeHtml(desc)}</p>`
      : `<p>${escapeHtml(nextTitle)}</p>`;
    setSaving(true);
    try {
      const sent = await dispatch(sendMessage({
        content,
        channelId: targetChannelId,
      })).unwrap();
      const messageId = sent?.id || sent?._id;
      if (!messageId) throw new Error('Workflow message was not created');
      await dispatch(addReaction({
        messageId,
        emoji: typeId,
        channelId: targetChannelId,
        userId: user?.id,
        workflowTitle: nextTitle,
        workflowDescription: desc,
        ...(spaceId ? { workflowSpaceId: spaceId } : {}),
        ...(spaceName ? { workflowSpaceName: spaceName } : {}),
      })).unwrap();
      dispatch(fetchTasks({}));
      dispatch(addNotification({
        type: 'success',
        message: `${getReactionDisplay(typeId)?.label || 'Workflow'} created`,
      }));
      setOpen(false);
      setTypeId('');
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || error?.message || 'Failed to create workflow',
      }));
    } finally {
      setSaving(false);
    }
  }, [dispatch, saving, typeId, user?.id]);

  return (
    <CreateWorkflowDialog
      isOpen={open}
      typeId={typeId}
      fromVoice
      saving={saving}
      onClose={() => {
        if (saving) return;
        setOpen(false);
        setTypeId('');
      }}
      onCreate={handleCreate}
    />
  );
};

export default CreateWorkflowDialog;
